import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState, setModalData } from "@/store/globalSlice"
import GuideGameMock from "@/Components/GuideGameMock"
import { buildGuideMock } from "@/utils/guideMockGame"
import { HelpGroupId, HelpSide, IHelpSpot, helpGroups, helpSpots } from "@/utils/helpSpots"

/**
 * The guide: a labelled diagram of the game screen, in a modal.
 *
 * It annotates a dead copy of the game (Components/GuideGameMock.tsx) rather
 * than the live board. Annotating the real thing sounds better than it plays:
 * it needs the board greyed out and click-swallowed while the player reads, it
 * can only ever explain what happens to be on screen at that moment, and every
 * label has to be re-measured as the game moves underneath it. The copy has
 * none of those problems, and it is made of the game's own components against
 * an invented game (utils/guideMockGame.ts) — so it stays a true picture of the
 * screen without anyone having to keep a drawing of it up to date, and it can
 * hold a state a real screen only reaches occasionally (a completed set, a
 * dropped player, offers on the table, a building ban) for as long as the
 * player wants to read about it.
 *
 * Reading it:
 *
 *   - "Everything" numbers every part of the screen with a bare dot, so the
 *     player can see at a glance that the whole thing is accounted for;
 *   - each of the other tabs cuts down to one part of the screen and puts a
 *     named label on each of its spots;
 *   - whatever is hovered — the label or the thing it points at — swaps for the
 *     full description, and the stepper walks the tab in order for players who
 *     would rather be led.
 *
 * Label placement is a solver, not a table of coordinates: each spot in
 * helpSpots.ts names the direction it would *like* its label to go, and
 * `resolve()` below pushes labels further out, or around to another side, until
 * none of them overlap. So the mock can be rearranged without a round of
 * re-tuning every label that neighbours the thing that moved.
 */

// Distance from a ringed element to its label, and what each extra lane adds.
const GAP = 14
const LANE_STEP = 32
// Keep labels this far inside the stage.
const EDGE = 8
// Room left around the mock for labels to live in. The mock is a full copy of
// the screen, so it is given as much of the stage as it can have and the labels
// take the strip left over (plus, where they have to, the dimmed parts of the
// copy itself — everything but the rings is behind a dim, so a label over it
// still reads). The labels are not scaled, so a smaller window gets a smaller
// diagram rather than smaller text.
const MARGIN_X = 90
const MARGIN_Y = 32
const MIN_SCALE = 0.32
// Re-measure this often. The mock is static, but web fonts and the piece images
// land a beat after the first paint and move things a pixel or two.
const SETTLE_INTERVAL = 400

interface Rect {
  left: number
  top: number
  width: number
  height: number
}

interface Box {
  left: number
  top: number
  w: number
  h: number
}

interface Placement {
  spot: IHelpSpot
  /** Stage-local rect of the element in the mock. */
  rect: Rect
  /** Where the label ended up, and where its pointer starts from. */
  box: Box
  side: HelpSide
  lane: number
  tipX: number
  tipY: number
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max))

/** Where a label hung off `rect` on `side`, `lane` steps out, points from. */
const anchorFor = (rect: Rect, side: HelpSide, lane: number) => {
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2
  const right = rect.left + rect.width
  const bottom = rect.top + rect.height
  const d = GAP + lane * LANE_STEP
  if (side === "top") return { x: cx, y: rect.top - d, tipX: cx, tipY: rect.top }
  if (side === "bottom") return { x: cx, y: bottom + d, tipX: cx, tipY: bottom }
  if (side === "left") return { x: rect.left - d, y: cy, tipX: rect.left, tipY: cy }
  return { x: right + d, y: cy, tipX: right, tipY: cy }
}

/** Top-left corner of a box of a known size hung off that anchor point. */
const boxOriginFor = (
  side: HelpSide,
  align: IHelpSpot["align"],
  x: number,
  y: number,
  w: number,
  h: number
): { left: number; top: number } => {
  if (side === "left") return { left: x - w, top: y - h / 2 }
  if (side === "right") return { left: x, top: y - h / 2 }
  const left = align === "start" ? x : align === "end" ? x - w : x - w / 2
  return { left, top: side === "top" ? y - h : y }
}

const overlapArea = (a: Box, b: Box, margin: number) => {
  const dx = Math.min(a.left + a.w + margin, b.left + b.w + margin) - Math.max(a.left - margin, b.left - margin)
  const dy = Math.min(a.top + a.h + margin, b.top + b.h + margin) - Math.max(a.top - margin, b.top - margin)
  return dx > 0 && dy > 0 ? dx * dy : 0
}

// Sides to try, in order, when the one a spot asked for is taken. Second choice
// is always the other axis rather than the exact opposite, so a crowded label
// steps sideways instead of jumping to the far side of the thing it labels.
const SIDE_FALLBACKS: Record<HelpSide, HelpSide[]> = {
  top: ["top", "left", "right", "bottom"],
  bottom: ["bottom", "right", "left", "top"],
  left: ["left", "top", "bottom", "right"],
  right: ["right", "bottom", "top", "left"],
}

const MAX_LANES = 6

/**
 * Place every label so that none of them overlap — each other, or the things
 * being pointed at.
 *
 * Greedy and order-dependent, which is what we want: the catalogue is written
 * in reading order, so the first spots in a group keep the position they asked
 * for and the ones that would have collided are the ones that get pushed. Cost
 * prefers, in order: the side the spot asked for, the shortest pointer, and no
 * overlap at all — with overlap weighted heavily enough that it only survives
 * when every one of the 24 candidates collides with something.
 *
 * The ringed elements are obstacles too, and that matters as much as the labels
 * are to each other: they are the only parts of the copy the dim leaves lit, so
 * a chip parked on one hides the very thing its neighbour's pointer is aimed
 * at. Keys that sit shoulder to shoulder — the row in the top-right corner — are
 * where this bites, and where the catalogue's own hint is usually wrong.
 */
const resolve = (
  items: { spot: IHelpSpot; rect: Rect; size: { w: number; h: number } }[],
  stageW: number,
  stageH: number
): Placement[] => {
  const placed: Box[] = []
  const out: Placement[] = []
  // Every ringed element, label or not, in the same shape as a label box.
  const rings: Box[] = items.map(({ rect }) => ({
    left: rect.left,
    top: rect.top,
    w: rect.width,
    h: rect.height,
  }))

  items.forEach(({ spot, rect, size }) => {
    const sides = SIDE_FALLBACKS[spot.side]
    let best: Placement | undefined
    let bestCost = Infinity

    for (let sideIndex = 0; sideIndex < sides.length; sideIndex++) {
      const side = sides[sideIndex]
      const baseLane = side === spot.side ? spot.lane || 0 : 0
      for (let step = 0; step < MAX_LANES; step++) {
        const lane = baseLane + step
        const a = anchorFor(rect, side, lane)
        const origin = boxOriginFor(side, spot.align, a.x, a.y, size.w, size.h)
        const left = clamp(origin.left, EDGE, Math.max(EDGE, stageW - size.w - EDGE))
        const top = clamp(origin.top, EDGE, Math.max(EDGE, stageH - size.h - EDGE))
        const box: Box = { left, top, w: size.w, h: size.h }

        let cost = sideIndex * 240 + step * 90
        // Being shoved back inside the stage means the label is no longer where
        // its pointer says it is, so it is worth about a lane of penalty.
        cost += (Math.abs(left - origin.left) + Math.abs(top - origin.top)) * 1.5
        placed.forEach((other) => {
          cost += overlapArea(box, other, 3) * 0.6
        })
        // Sitting on a lit element costs about as much as sitting on another
        // label. Its own element is in this list too: a label shoved back inside
        // the stage can land on the thing it came from, which reads worse than a
        // longer pointer.
        rings.forEach((ring) => {
          cost += overlapArea(box, ring, 3) * 0.55
        })

        if (cost < bestCost) {
          bestCost = cost
          best = { spot, rect, box, side, lane, tipX: a.tipX, tipY: a.tipY }
        }
      }
    }

    if (best) {
      placed.push(best.box)
      out.push(best)
    }
  })

  return out
}

const signature = (placements: Placement[], scale: number) =>
  `${scale.toFixed(3)}|` +
  placements
    .map((p) => `${p.spot.id}:${Math.round(p.box.left)},${Math.round(p.box.top)},${Math.round(p.rect.left)},${Math.round(p.rect.top)}`)
    .join("|")

const GuideModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const active = useAppSelector((state) => state.global.helpTourActive)

  // Anything the game is actually waiting on takes the screen back. Reading the
  // guide is never urgent; an auction closing while the player is in here is.
  const gameNeedsScreen = useAppSelector((state) => {
    const modals = state.global.modalData
    return Boolean(
      modals.auction?.visible ||
        modals.buyOrAuction?.visible ||
        modals.decisionHub?.visible ||
        modals.stockExchange?.visible ||
        modals.jail?.visible ||
        modals.bankrupt?.visible ||
        modals.trade?.visible ||
        modals.propMng?.visible ||
        state.global.pendingDecision ||
        state.global.myGame?.situation?.gameOver
    )
  })

  // The copy is of *this* player's game as far as the board itself goes: same
  // map, same background. Everything on it (the seats, the money, the deals) is
  // invented — see utils/guideMockGame.ts.
  const mapId = useAppSelector((state) => state.global.myGame?.settings.mapId)
  const themeId = useAppSelector((state) => state.global.selectedTheme?.id)
  const themeName = useAppSelector((state) => state.global.selectedTheme?.name)
  const mock = useMemo(
    () =>
      buildGuideMock(
        mapId,
        themeId ? { id: themeId, name: themeName || "", image: themeId } : undefined
      ),
    [mapId, themeId, themeName]
  )

  const [group, setGroup] = useState<HelpGroupId | "all">("all")
  const [focusedId, setFocusedId] = useState<string | null>(null)
  // Set while the stepper is driving, so walking the tour is not undone by the
  // cursor happening to rest somewhere else.
  const [stepping, setStepping] = useState(false)

  const [scale, setScale] = useState(MIN_SCALE)
  const [placements, setPlacements] = useState<Placement[]>([])

  const stageRef = useRef<HTMLDivElement>(null)
  const mockRef = useRef<HTMLDivElement>(null)
  const labelRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const signatureRef = useRef("")

  const shownSpots = useMemo(
    () => (group === "all" ? helpSpots : helpSpots.filter((spot) => spot.group === group)),
    [group]
  )
  // "Everything" is a map, not a reading list: twenty named labels at once is a
  // wall of text, so that tab numbers the spots and lets the description card do
  // the talking. A single group is small enough to name every one of its parts.
  const showNames = group !== "all"

  const close = useCallback(() => {
    dispatch(setState({ helpTourActive: false }))
  }, [dispatch])

  // ---------------------------------------------------------------------
  // Measure, scale to fit, then solve the label layout. All in one pass so a
  // resize can never leave rings and labels a frame out of step.
  // ---------------------------------------------------------------------
  const relayout = useCallback(() => {
    const stageEl = stageRef.current
    const mockEl = mockRef.current
    if (!stageEl || !mockEl) return

    const stageRect = stageEl.getBoundingClientRect()
    if (stageRect.width < 40 || stageRect.height < 40) return

    // The copy is laid out at window size (see GuideGameMock), so that is what
    // it has to be scaled down from.
    const nextScale = clamp(
      Math.min(
        1,
        (stageRect.width - MARGIN_X * 2) / window.innerWidth,
        (stageRect.height - MARGIN_Y * 2) / window.innerHeight
      ),
      MIN_SCALE,
      1
    )

    const items = shownSpots.flatMap((spot) => {
      const selector = mock.selectors[spot.id]
      // A spot whose element is not on this screen — a key only solo games get,
      // a panel that only opens sometimes — is left off rather than pointed at
      // nothing.
      const el = selector ? mockEl.querySelector<HTMLElement>(selector) : null
      if (!el) return []
      const r = el.getBoundingClientRect()
      if (r.width < 3 || r.height < 3) return []
      const label = labelRefs.current[spot.id]
      return [
        {
          spot,
          rect: {
            left: r.left - stageRect.left,
            top: r.top - stageRect.top,
            width: r.width,
            height: r.height,
          },
          size: {
            w: label?.offsetWidth || (showNames ? 120 : 24),
            h: label?.offsetHeight || 24,
          },
        },
      ]
    })

    const next = resolve(items, stageRect.width, stageRect.height)
    const key = signature(next, nextScale)
    if (key === signatureRef.current) return
    signatureRef.current = key
    setScale(nextScale)
    setPlacements(next)
  }, [shownSpots, showNames, mock])

  useLayoutEffect(() => {
    if (!active) {
      signatureRef.current = ""
      setPlacements([])
      return
    }
    relayout()
    const observer = new ResizeObserver(() => relayout())
    if (stageRef.current) observer.observe(stageRef.current)
    const interval = window.setInterval(relayout, SETTLE_INTERVAL)
    return () => {
      observer.disconnect()
      window.clearInterval(interval)
    }
    // `scale` is a dependency because the first pass measures the mock at
    // whatever scale the last open left behind, and settles on the right one in
    // the same breath. Re-running on the new scale is what turns that into a
    // second pass against the geometry the player will actually see — without
    // it the rings would sit on the old layout until the settle interval came
    // round. Both passes land inside one commit, so nothing paints misaligned.
  }, [active, relayout, scale])

  // A fresh open starts over rather than resuming wherever the last one ended.
  useEffect(() => {
    if (active) return
    setGroup("all")
    setFocusedId(null)
    setStepping(false)
  }, [active])

  useEffect(() => {
    if (active && gameNeedsScreen) close()
  }, [active, gameNeedsScreen, close])

  const focusedIndex = placements.findIndex((p) => p.spot.id === focusedId)
  const focused = focusedIndex >= 0 ? placements[focusedIndex] : null

  const step = useCallback(
    (delta: number) => {
      if (!placements.length) return
      const from = placements.findIndex((p) => p.spot.id === focusedId)
      const next =
        from < 0
          ? delta > 0
            ? 0
            : placements.length - 1
          : (from + delta + placements.length) % placements.length
      setStepping(true)
      setFocusedId(placements[next].spot.id)
    },
    [placements, focusedId]
  )

  // Switching tabs drops a focus that is no longer on screen.
  useEffect(() => {
    if (focusedId && placements.length && !placements.some((p) => p.spot.id === focusedId)) {
      setFocusedId(null)
    }
  }, [placements, focusedId])

  useEffect(() => {
    if (!active) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        close()
        return
      }
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault()
        step(1)
        return
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault()
        step(-1)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [active, close, step])

  // ---------------------------------------------------------------------
  // The description card, hung off the focused label's own anchor and then
  // clamped into the stage — measured and placed inside one layout effect so it
  // never paints where it does not belong.
  // ---------------------------------------------------------------------
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardPos, setCardPos] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const stageEl = stageRef.current
    if (!focused || !cardRef.current || !stageEl) {
      setCardPos(null)
      return
    }
    const w = cardRef.current.offsetWidth
    const h = cardRef.current.offsetHeight
    const a = anchorFor(focused.rect, focused.side, focused.lane)
    const origin = boxOriginFor(focused.side, focused.spot.align, a.x, a.y, w, h)
    const { width, height } = stageEl.getBoundingClientRect()
    setCardPos({
      left: clamp(origin.left, EDGE, Math.max(EDGE, width - w - EDGE)),
      top: clamp(origin.top, EDGE, Math.max(EDGE, height - h - EDGE)),
    })
  }, [focused])

  if (!active) return null

  const activeGroup = helpGroups.find((g) => g.id === group)
  const openFullGuide = () => {
    close()
    dispatch(setModalData({ help: { visible: true } }))
  }

  const focusSpot = (id: string, pinned: boolean) => {
    setStepping(pinned)
    setFocusedId(id)
  }

  return createPortal(
    <div
      className="guide-modal-root fixed inset-0 z-[420] flex items-center justify-center bg-black/80 p-3 font-poppins backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="flex h-full w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_90px_rgba(0,0,0,0.75)]"
        style={{ background: "linear-gradient(160deg,#15130f 0%,#0a0908 100%)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="game.guide-modal"
        role="dialog"
        aria-label="Guide to the game screen"
      >
        {/* ---------------------------------------------------------------- */}
        {/* Header                                                           */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-400/10">
              <i className="fa-solid fa-circle-question text-base text-amber-300"></i>
            </span>
            <div className="leading-tight">
              <h2 className="text-base font-bold tracking-wide text-white">
                What everything on the screen does
              </h2>
              <p className="text-[0.6875rem] text-white/40">
                A labelled copy of the board. Nothing in here is live — your game is untouched.
              </p>
            </div>
          </div>

          <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5">
            {([{ id: "all" as const, label: "Everything", icon: "fa-border-all" }, ...helpGroups]).map(
              (g) => {
                const isActive = group === g.id
                const count =
                  g.id === "all"
                    ? helpSpots.length
                    : helpSpots.filter((spot) => spot.group === g.id).length
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setGroup(g.id as HelpGroupId | "all")}
                    data-testid={`game.guide-group.${g.id}`}
                    className={classNames(
                      "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150",
                      isActive
                        ? "bg-amber-400 text-[#2b1c02]"
                        : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/90"
                    )}
                  >
                    <i className={classNames("fa-solid text-[0.6875rem]", g.icon)}></i>
                    <span>{g.label}</span>
                    <span
                      className={classNames(
                        "rounded-full px-1.5 py-px text-[0.625rem] leading-none",
                        isActive ? "bg-[#2b1c02]/20" : "bg-black/40"
                      )}
                    >
                      {count}
                    </span>
                  </button>
                )
              }
            )}

            <button
              type="button"
              onClick={close}
              aria-label="Close"
              data-testid="game.guide-close"
              className="ml-1 flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-lg bg-white/5 text-[20px] text-white/60 transition-colors duration-150 hover:bg-white/15 hover:text-white"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Stage: the mock, dimmed except where the pointers are, plus the    */}
        {/* labels laid over it.                                              */}
        {/* ---------------------------------------------------------------- */}
        <div ref={stageRef} className="relative flex-1 overflow-hidden">
          <div
            ref={mockRef}
            className={classNames(
              "absolute left-1/2 top-1/2 transition-opacity duration-200",
              placements.length ? "opacity-100" : "opacity-0"
            )}
            // Window-sized and then scaled: the copy has to be laid out at the
            // size the game is laid out at to be a copy of it at all.
            style={{
              width: "100vw",
              height: "100vh",
              transform: `translate(-50%,-50%) scale(${scale})`,
            }}
          >
            <GuideGameMock mock={mock} />
          </div>

          {/* The dim, with a hole punched over everything being pointed at. */}
          <svg className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
            <defs>
              <mask id="guide-modal-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
                {placements.map((p) => (
                  <rect
                    key={p.spot.id}
                    x={p.rect.left - 3}
                    y={p.rect.top - 3}
                    width={p.rect.width + 6}
                    height={p.rect.height + 6}
                    rx="6"
                    fill="#000"
                  />
                ))}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(4,5,10,0.72)"
              mask="url(#guide-modal-mask)"
            />
            {/* The pointers. Each one is drawn twice: a dark casing first, then
                the bright line down the middle of it. The dim behind them is
                only 0.72, so a bare thin line crosses a lit board tile, a white
                player card and a black background in the same run and goes
                missing over at least one of them — the casing is what keeps the
                same line readable over all three. */}
            {placements.map((p) => {
              const isFocused = p.spot.id === focusedId
              const x2 = p.box.left + p.box.w / 2
              const y2 = p.box.top + p.box.h / 2
              return (
                <g key={p.spot.id}>
                  <line
                    x1={p.tipX}
                    y1={p.tipY}
                    x2={x2}
                    y2={y2}
                    stroke="rgba(0,0,0,0.75)"
                    strokeWidth={isFocused ? 5.5 : 4}
                    strokeLinecap="round"
                  />
                  <line
                    x1={p.tipX}
                    y1={p.tipY}
                    x2={x2}
                    y2={y2}
                    stroke={isFocused ? "#fde047" : "#fbbf24"}
                    strokeWidth={isFocused ? 2.5 : 1.75}
                    strokeLinecap="round"
                  />
                  {/* The end that matters: a ringed dot on the element, so the
                      pointer is unmistakably attached to something even where it
                      runs over a busy tile. */}
                  <circle
                    cx={p.tipX}
                    cy={p.tipY}
                    r={isFocused ? 4 : 3}
                    fill={isFocused ? "#fde047" : "#fbbf24"}
                    stroke="rgba(0,0,0,0.75)"
                    strokeWidth={1.5}
                  />
                </g>
              )
            })}
          </svg>

          {/* The rings. Pointing at the confusing thing is what players try
              first, so hovering one of these does the same as hovering its
              label. */}
          {placements.map((p) => {
            const isFocused = p.spot.id === focusedId
            return (
              <div
                key={p.spot.id}
                className={classNames(
                  "absolute cursor-help rounded-md transition-[box-shadow,border-color] duration-150",
                  isFocused
                    ? "guide-ring-active border-2 border-amber-300"
                    : "border border-amber-300/50 hover:border-amber-300"
                )}
                style={{
                  left: p.rect.left - 3,
                  top: p.rect.top - 3,
                  width: p.rect.width + 6,
                  height: p.rect.height + 6,
                }}
                onMouseEnter={() => focusSpot(p.spot.id, false)}
                onMouseLeave={() => {
                  if (!stepping) {
                    setFocusedId((current) => (current === p.spot.id ? null : current))
                  }
                }}
                onClick={() => focusSpot(p.spot.id, true)}
              ></div>
            )
          })}

          {/* The labels. Always rendered, so their measured size feeds the
              layout solver and the numbering never shifts as focus moves; the
              focused one is hidden because its description card stands in the
              same place. */}
          {shownSpots.map((spot) => {
            const index = placements.findIndex((p) => p.spot.id === spot.id)
            const placement = index >= 0 ? placements[index] : undefined
            const isFocused = spot.id === focusedId
            return (
              <div
                key={spot.id}
                ref={(el) => {
                  labelRefs.current[spot.id] = el
                }}
                className={classNames(
                  "guide-chip absolute flex cursor-help items-center gap-1.5 whitespace-nowrap rounded-full border border-amber-300/40 bg-[#140f06]/95 shadow-[0_4px_14px_rgba(0,0,0,0.5)] transition-colors duration-150 hover:border-amber-300 hover:bg-[#231803]/95",
                  showNames ? "py-1 pl-1 pr-2.5" : "p-1",
                  // `invisible`, not `opacity-0`: the entrance animation below
                  // fills its own opacity forwards, which would win against an
                  // opacity class and flash an unplaced label at the origin.
                  (!placement || isFocused) && "invisible"
                )}
                style={{ left: placement?.box.left ?? 0, top: placement?.box.top ?? 0 }}
                onMouseEnter={() => focusSpot(spot.id, false)}
                onMouseLeave={() => {
                  if (!stepping) {
                    setFocusedId((current) => (current === spot.id ? null : current))
                  }
                }}
                onClick={() => focusSpot(spot.id, true)}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[0.625rem] font-bold leading-none text-[#2b1c02]">
                  {index + 1}
                </span>
                {showNames && (
                  <span className="text-[0.6875rem] font-semibold leading-none tracking-wide text-amber-100/90">
                    {spot.label}
                  </span>
                )}
              </div>
            )
          })}

          {/* The expanded one. */}
          {focused && (
            <div
              ref={cardRef}
              className={classNames(
                "guide-card pointer-events-none absolute w-[20rem] max-w-[80%] rounded-xl border border-amber-300/40 p-3.5 shadow-[0_16px_40px_rgba(0,0,0,0.7)] backdrop-blur-md",
                !cardPos && "opacity-0"
              )}
              style={{
                left: cardPos ? cardPos.left : 0,
                top: cardPos ? cardPos.top : 0,
                background: "linear-gradient(150deg, rgba(26,20,8,0.98) 0%, rgba(9,7,3,0.98) 100%)",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-400 text-[0.6875rem] font-bold leading-none text-[#2b1c02]">
                  {focusedIndex + 1}
                </span>
                <i className={classNames("fa-solid text-xs text-amber-300/80", focused.spot.icon)}></i>
                <span className="text-[1rem] font-bold leading-tight tracking-wide text-amber-200">
                  {focused.spot.label}
                </span>
              </div>

              <p className="mt-2 text-[0.875rem] leading-snug text-white/75">
                {focused.spot.description}
              </p>

              {focused.spot.tip && (
                <div className="mt-2.5 flex items-start gap-2 rounded-md bg-amber-400/10 px-2 py-2 text-[0.8125rem] font-medium leading-snug text-amber-200/90">
                  <i className="fa-solid fa-lightbulb mt-[3px] shrink-0 text-[0.6875rem]"></i>
                  <span>{focused.spot.tip}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Footer                                                            */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/10 px-4 py-2.5">
          <p className="min-w-0 flex-1 text-[0.75rem] leading-snug text-white/45">
            {activeGroup
              ? activeGroup.blurb
              : "Every part of the screen, numbered. Hover a dot — or the thing it points at — to find out what it is."}
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous"
              data-testid="game.guide-prev"
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-white/70 transition-colors duration-150 hover:bg-white/15 hover:text-white"
            >
              <i className="fa-solid fa-chevron-left text-[0.6875rem]"></i>
            </button>
            <span className="min-w-[4rem] text-center text-xs font-semibold tabular-nums text-white/60">
              {focusedIndex >= 0
                ? `${focusedIndex + 1} / ${placements.length}`
                : `${placements.length} spots`}
            </span>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next"
              data-testid="game.guide-next"
              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-white/70 transition-colors duration-150 hover:bg-white/15 hover:text-white"
            >
              <i className="fa-solid fa-chevron-right text-[0.6875rem]"></i>
            </button>
          </div>

          <button
            type="button"
            onClick={openFullGuide}
            data-testid="game.guide-full-guide"
            className="flex items-center gap-1.5 rounded-lg bg-white/5 px-2.5 py-1.5 text-xs font-semibold text-white/70 transition-colors duration-150 hover:bg-white/15 hover:text-white"
          >
            <i className="fa-solid fa-book-open text-[0.6875rem]"></i>
            <span>Read the full guide</span>
          </button>

          <span className="hidden text-[0.625rem] text-white/30 lg:inline">Esc to close · ← → to step</span>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default GuideModal
